import { useEffect, useState } from "react";
import axios from "axios";
import { Cloud, CloudRain, CloudSnow, Sun, CloudSun, Wind, Sunrise, Sunset, Thermometer, Droplets } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function iconFor(label) {
  if (!label) return Cloud;
  const l = label.toLowerCase();
  if (l.includes("regn")) return CloudRain;
  if (l.includes("snö")) return CloudSnow;
  if (l.includes("klart") && !l.includes("mest")) return Sun;
  if (l.includes("mest klart") || l.includes("halvklart") || l.includes("växlande")) return CloudSun;
  return Cloud;
}

const SV_SHORT_DAYS = ["sön", "mån", "tis", "ons", "tor", "fre", "lör"];

export default function SmhiWidget({ lat, lng, placeName = "platsen" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (lat == null || lng == null) return;
    setLoading(true);
    axios.get(`${API}/smhi/forecast`, { params: { lat, lng } })
      .then(r => {
        if (r.data && r.data.error) setErr(r.data.error);
        else setData(r.data);
      })
      .catch(() => setErr("network"))
      .finally(() => setLoading(false));
  }, [lat, lng]);

  if (lat == null || lng == null) return null;
  if (loading) {
    return (
      <div data-testid="smhi-widget-loading" className="bg-parchment border border-warm-border rounded-sm p-5 sm:p-6 mb-6 animate-pulse">
        <div className="h-3 bg-bone-alt w-32 mb-3 rounded" />
        <div className="h-16 bg-bone-alt rounded" />
      </div>
    );
  }
  if (err || !data || !data.current) {
    return (
      <div data-testid="smhi-widget-error" className="bg-parchment border border-warm-border rounded-sm p-4 mb-6 text-xs text-bark/60">
        Vädret är inte tillgängligt just nu.
      </div>
    );
  }

  const cur = data.current;
  const Icon = iconFor(cur.wsymb_label);
  const sun = data.sun || {};

  const decision =
    cur.precip_mean > 1 ? "Ta regnkläder – det blir blött."
    : cur.temp != null && cur.temp < -10 ? "Klä dig varmt – det är rejält kallt."
    : cur.wind_speed != null && cur.wind_speed > 10 ? "Vindigt idag – ta vindtät jacka."
    : (cur.wsymb_label || "").toLowerCase().includes("klart") ? "Fint väder – perfekt att gå ut."
    : "Helt OK väder för en tur.";

  return (
    <section data-testid="smhi-widget" className="bg-parchment border border-warm-border rounded-sm p-5 sm:p-6 mb-6">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[10px] font-body font-semibold text-bark/55 uppercase tracking-[0.22em] mb-0.5">Går jag idag?</p>
          <p className="font-fraktur-smooth text-lg sm:text-xl text-falu tracking-wide">Väder vid {placeName}</p>
        </div>
        <div className="text-right">
          <p className="font-heading text-3xl font-semibold text-ink leading-none">
            {cur.temp != null ? `${Math.round(cur.temp)}°` : "—"}
          </p>
          <p className="text-[11px] text-bark/60 mt-0.5">{cur.wsymb_label || ""}</p>
        </div>
      </div>

      <div className="bg-pine/5 border-l-4 border-pine rounded-sm px-3 py-2 mb-4">
        <p className="text-sm text-ink font-body">
          <Icon size={14} className="inline mr-1.5 -mt-0.5 text-pine" />
          {decision}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
        {cur.wind_speed != null && (
          <div className="flex items-center gap-1.5 text-bark/75"><Wind size={13} /> {Math.round(cur.wind_speed)} m/s{cur.wind_gust != null && cur.wind_gust > cur.wind_speed + 2 ? ` (bvb ${Math.round(cur.wind_gust)})` : ""}</div>
        )}
        {cur.precip_mean != null && (
          <div className="flex items-center gap-1.5 text-bark/75"><Droplets size={13} /> {cur.precip_mean.toFixed(1)} mm/h</div>
        )}
        {sun.rise && (
          <div className="flex items-center gap-1.5 text-bark/75"><Sunrise size={13} /> {sun.rise}</div>
        )}
        {sun.set && (
          <div className="flex items-center gap-1.5 text-bark/75"><Sunset size={13} /> {sun.set}</div>
        )}
      </div>

      {data.daily && data.daily.length > 0 && (
        <div data-testid="smhi-daily" className="grid grid-cols-5 gap-2 border-t border-warm-border pt-3">
          {data.daily.slice(0, 5).map(d => {
            const DIcon = iconFor(d.wsymb_label);
            const day = new Date(d.date + "T12:00:00");
            return (
              <div key={d.date} className="text-center">
                <p className="text-[10px] font-body font-semibold text-bark/55 uppercase tracking-wider mb-1">
                  {SV_SHORT_DAYS[day.getDay()] || ""}
                </p>
                <DIcon size={18} className="mx-auto text-pine opacity-80 mb-1" />
                <p className="text-sm font-semibold text-ink">{d.temp != null ? `${Math.round(d.temp)}°` : "—"}</p>
                {d.precip_mean != null && d.precip_mean > 0.1 && (
                  <p className="text-[10px] text-bark/55">{d.precip_mean.toFixed(1)}mm</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-3 text-[10px] text-bark/45 italic">
        Källa: <a href="https://opendata.smhi.se" target="_blank" rel="noopener noreferrer" className="hover:text-pine">SMHI Open Data (snow1gv1)</a> · uppdateras var 30:e min
      </p>
    </section>
  );
}

export { iconFor as smhiIconFor };
